import React, { useState } from "react";
import { ListFilter } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface StaffOption {
  value: string;
  label: string;
}

interface StaffFilterButtonProps {
  options: StaffOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
}

export const StaffFilterButton: React.FC<StaffFilterButtonProps> = ({
  options,
  value,
  onValueChange,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleToggle = (optionValue: string, checked: boolean) => {
    onValueChange(
      checked
        ? [...value, optionValue]
        : value.filter((v) => v !== optionValue),
    );
  };

  const label =
    value.length > 0
      ? `${t("common.filter")} (${value.length})`
      : t("common.filter");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="icon-text"
          className="gap-2 min-h-8 h-8 py-1 border border-slate-200 text-foreground text-sm font-medium shadow-xs"
        >
          <ListFilter className="size-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <div className="max-h-[240px] overflow-y-auto py-1">
          {options.length === 0 && (
            <p className="text-xs text-muted-foreground px-3 py-2">
              {t("staffSchedule.schedule.empty")}
            </p>
          )}
          {options.map((option) => {
            const isSelected = value.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-muted/50"
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={(checked) =>
                    handleToggle(option.value, checked as boolean)
                  }
                />
                <span className="text-sm truncate">{option.label}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
